import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  type ReactNode,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useQuery } from "react-query";
import { useSnackbar } from "notistack";
import {
  getFeatureFlags,
  type FeatureFlagsSnapshot,
  type FeatureKey,
  type FlagState,
} from "../services/feature-flags";

/**
 * Дефолтный снапшот, пока флаги ещё не подгрузились —
 * считаем всё включённым, чтобы не блокировать UI.
 */
const DEFAULT_FLAG: FlagState = { enabled: true, message: null, disabledUntil: null };
const DEFAULT_SNAPSHOT: FeatureFlagsSnapshot = {
  global: DEFAULT_FLAG,
  features: {
    deposit: DEFAULT_FLAG,
    withdraw: DEFAULT_FLAG,
    sbp_qr: DEFAULT_FLAG,
    exchange: DEFAULT_FLAG,
    cards: DEFAULT_FLAG,
    card_issue: DEFAULT_FLAG,
    aml: DEFAULT_FLAG,
    services_payment: DEFAULT_FLAG,
    swift: DEFAULT_FLAG,
    foreign_card: DEFAULT_FLAG,
  },
};

interface FeatureFlagsContextValue {
  snapshot: FeatureFlagsSnapshot;
  isReady: boolean;
  /** Функция доступна? Учитывает глобальный флаг. */
  isFeatureEnabled: (key: FeatureKey) => boolean;
  /** Сообщение админа для отключённой фичи (может быть null). */
  getFeatureMessage: (key: FeatureKey) => string | null;
  /**
   * Хелпер для UI: если фича выключена — показывает snackbar и
   * возвращает `false` (значит клик обрабатывать не надо).
   * Если включена — возвращает `true`.
   */
  guardFeature: (key: FeatureKey) => boolean;
}

const FeatureFlagsContext = createContext<FeatureFlagsContextValue | null>(null);

/** Как часто дёргаем /feature-flags — 30 сек, чтобы админский тумблер срабатывал быстро. */
const POLL_INTERVAL_MS = 30_000;

export function FeatureFlagsProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const location = useLocation();
  const { enqueueSnackbar } = useSnackbar();

  const { data, isFetched } = useQuery({
    queryKey: ["feature-flags"],
    queryFn: getFeatureFlags,
    refetchInterval: POLL_INTERVAL_MS,
    refetchOnWindowFocus: true,
    // Даже если рухнёт — не спамим ошибками, тихо считаем всё включённым.
    retry: 1,
  });

  const snapshot = data ?? DEFAULT_SNAPSHOT;
  const isReady = isFetched;

  // Автоматический редирект на /maintenance при глобальном тех-режиме.
  useEffect(() => {
    if (!data) return;
    const inMaintenance = data.global.enabled === false;

    if (inMaintenance && location.pathname !== "/maintenance") {
      navigate("/maintenance", { replace: true });
      return;
    }
    // Если админ выключил maintenance, а юзер завис на /maintenance —
    // возвращаем на главную.
    if (!inMaintenance && location.pathname === "/maintenance") {
      navigate("/", { replace: true });
    }
  }, [data, location.pathname, navigate]);

  const isFeatureEnabled = useCallback(
    (key: FeatureKey) => {
      if (!snapshot.global.enabled) return false;
      return snapshot.features[key]?.enabled ?? true;
    },
    [snapshot],
  );

  const getFeatureMessage = useCallback(
    (key: FeatureKey) => {
      // Глобальное сообщение имеет приоритет.
      if (!snapshot.global.enabled) {
        return snapshot.global.message ?? "Ведутся технические работы";
      }
      const f = snapshot.features[key];
      if (f && !f.enabled) {
        return f.message ?? "Функция временно недоступна";
      }
      return null;
    },
    [snapshot],
  );

  const guardFeature = useCallback(
    (key: FeatureKey): boolean => {
      if (isFeatureEnabled(key)) return true;
      const msg = getFeatureMessage(key) ?? "Функция временно недоступна";
      enqueueSnackbar(msg, { variant: "warning" });
      return false;
    },
    [isFeatureEnabled, getFeatureMessage, enqueueSnackbar],
  );

  const value = useMemo<FeatureFlagsContextValue>(
    () => ({ snapshot, isReady, isFeatureEnabled, getFeatureMessage, guardFeature }),
    [snapshot, isReady, isFeatureEnabled, getFeatureMessage, guardFeature],
  );

  return (
    <FeatureFlagsContext.Provider value={value}>
      {children}
    </FeatureFlagsContext.Provider>
  );
}

export function useFeatureFlags(): FeatureFlagsContextValue {
  const ctx = useContext(FeatureFlagsContext);
  if (!ctx) {
    throw new Error("useFeatureFlags must be used within FeatureFlagsProvider");
  }
  return ctx;
}
